export type AgentTurnOutcome = "completed" | "aborted" | "error";

type AgentFirstTokenOutcome = "shown" | AgentTurnOutcome;

export type AgentSurface = "web" | "addin";

export interface AgentSpan {
  setAttribute: (key: string, value: string | number) => void;
  end: (endTime?: Date) => void;
}

export type StartInactiveSpan = (config: {
  name: string;
  op: string;
  attributes: Record<string, string>;
  startTime: Date;
}) => AgentSpan;

export interface AgentTurnTrace {
  markFirstVisible: () => void;
  finish: (outcome: AgentTurnOutcome) => void;
}

export interface DocumentVisibility {
  isHidden: () => boolean;
  subscribe: (onChange: () => void) => () => void;
}

export interface VisibleClock {
  read: () => number;
  stop: () => void;
}

export function documentVisibility(): DocumentVisibility {
  return {
    isHidden: () => document.visibilityState === "hidden",
    subscribe(onChange) {
      document.addEventListener("visibilitychange", onChange);
      return function unsubscribe() {
        document.removeEventListener("visibilitychange", onChange);
      };
    },
  };
}

export function _startVisibleClock(
  now: () => number,
  visibility: DocumentVisibility,
): VisibleClock {
  const start = now();
  let hiddenSince: number | null = visibility.isHidden() ? start : null;
  let hiddenTotal = 0;

  function handleVisibilityChange() {
    if (visibility.isHidden()) {
      if (hiddenSince === null) hiddenSince = now();
      return;
    }
    if (hiddenSince !== null) {
      hiddenTotal += now() - hiddenSince;
      hiddenSince = null;
    }
  }

  const unsubscribe = visibility.subscribe(handleVisibilityChange);

  return {
    read() {
      const at = now();
      const ongoingHidden = hiddenSince === null ? 0 : at - hiddenSince;
      return at - start - hiddenTotal - ongoingHidden;
    },
    stop: unsubscribe,
  };
}

export function isUserVisibleAgentEvent(event: {
  type?: string;
  text?: string;
  tool_name?: string;
}): boolean {
  return Boolean(
    (event.type === "text" && event.text?.trim()) ||
      (event.type === "tool_call" && event.tool_name) ||
      event.type === "tool_result" ||
      (event.type === "status" && event.text?.trim()),
  );
}

export function isAgentErrorEvent(event: { type?: string }): boolean {
  return event.type === "error";
}

export function startAgentTurnTrace(
  startInactiveSpan: StartInactiveSpan,
  {
    surface,
    mode,
    model,
    visibility,
  }: {
    surface: AgentSurface;
    mode: string | null;
    model: string;
    visibility: DocumentVisibility;
  },
): AgentTurnTrace {
  const startTime = new Date();
  const clock = _startVisibleClock(() => performance.now(), visibility);
  const attributes = {
    "agent.surface": surface,
    "agent.mode": mode ?? "default",
    "agent.model": model,
  };
  const turn = startInactiveSpan({
    name: "agent.chat.turn",
    op: "gen_ai.chat",
    attributes,
    startTime,
  });
  const firstToken = startInactiveSpan({
    name: "agent.chat.first_token",
    op: "gen_ai.first_token",
    attributes,
    startTime,
  });
  let firstTokenEnded = false;

  function visibleEndTime(): Date {
    return new Date(startTime.getTime() + clock.read());
  }

  function endFirstToken(outcome: AgentFirstTokenOutcome) {
    if (firstTokenEnded) return;
    firstTokenEnded = true;
    firstToken.setAttribute("agent.first_token.outcome", outcome);
    firstToken.end(visibleEndTime());
  }

  return {
    markFirstVisible() {
      endFirstToken("shown");
    },
    finish(outcome) {
      endFirstToken(outcome);
      turn.setAttribute("agent.outcome", outcome);
      turn.end(visibleEndTime());
      clock.stop();
    },
  };
}

export function traceAgentStream(
  turn: AgentTurnTrace,
  source: ReadableStream<Uint8Array>,
  abortSignal?: AbortSignal,
): ReadableStream<Uint8Array> {
  const reader = source.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let errorSeen = false;
  let finished = false;

  const finishOnce = (outcome: AgentTurnOutcome) => {
    if (finished) return;
    finished = true;
    turn.finish(outcome);
  };

  const inspect = (line: string) => {
    if (!line.trim()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(line);
    } catch {
      errorSeen = true;
      return;
    }
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      errorSeen = true;
      return;
    }
    const record = parsed as Record<string, unknown>;
    if (
      ("type" in record && typeof record.type !== "string") ||
      ("text" in record && typeof record.text !== "string") ||
      ("tool_name" in record && typeof record.tool_name !== "string")
    ) {
      errorSeen = true;
      return;
    }
    const event = record as { type?: string; text?: string; tool_name?: string };
    if (isUserVisibleAgentEvent(event)) turn.markFirstVisible();
    if (isAgentErrorEvent(event)) errorSeen = true;
  };

  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        const { done, value } = await reader.read();
        if (done) {
          inspect(buffer);
          finishOnce(errorSeen ? "error" : "completed");
          controller.close();
          return;
        }
        buffer += decoder.decode(value, { stream: true });
        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          inspect(buffer.slice(0, newlineIndex));
          buffer = buffer.slice(newlineIndex + 1);
        }
        controller.enqueue(value);
      } catch (error) {
        finishOnce(abortSignal?.aborted ? "aborted" : "error");
        controller.error(error);
      }
    },
    cancel(reason) {
      finishOnce("aborted");
      return reader.cancel(reason);
    },
  });
}
